// FloorPlanPlacementTool.tsx
// Interactive floor plan placement with real distance measurements

export interface FloorPlan {
  id: string;
  name: string;
  dimensions: {
    width: number;  // feet
    depth: number;  // feet
  };
  squareFootage: number;
  bedrooms?: number;
  bathrooms?: number;
  stories?: number;
  imageUrl?: string;
  walls?: any[]; // Wall data from floor plan designer
  rooms?: any[]; // Room data from floor plan designer
  outline?: Array<{x: number, y: number}>; // Relative coordinates for complex shapes
}

export class FloorPlanPlacementTool {
  private map: google.maps.Map;
  private propertyBoundary: google.maps.LatLng[];
  private floorPlanOverlay: google.maps.GroundOverlay | google.maps.Polygon | null = null;
  private distanceMarkers: google.maps.Marker[] = [];
  private distanceLines: google.maps.Polyline[] = [];
  private infoWindow: google.maps.InfoWindow;
  private setbackViolations: string[] = [];
  private selectedFloorPlan: FloorPlan | null = null;
  private requiredSetbacks = {
    front: 25,
    rear: 20,
    side: 10
  };
  private rotation: number = 0; // degrees
  private isDragging: boolean = false;
  
  constructor(map: google.maps.Map, propertyBoundary: Array<{lat: number, lng: number}>) {
    this.map = map;
    this.propertyBoundary = propertyBoundary.map(coord => 
      new google.maps.LatLng(coord.lat, coord.lng)
    );
    this.infoWindow = new google.maps.InfoWindow();
    
    // Add floor plan selector
    this.createFloorPlanSelector();
  }
  
  /**
   * Load saved floor plans from localStorage
   */
  private loadSavedFloorPlans(): FloorPlan[] {
    const floorPlans: FloorPlan[] = [];
    
    // Get the list of saved floor plans
    const floorPlansList = JSON.parse(localStorage.getItem('floorPlansList') || '[]');
    
    // Load each floor plan from localStorage
    floorPlansList.forEach((planInfo: any) => {
      const planData = localStorage.getItem(`floorPlan_${planInfo.id}`);
      if (planData) {
        try {
          const parsed = JSON.parse(planData);
          
          // First check if dimensions are explicitly saved with the floor plan
          // Note: dimensions object has 'length' and 'width' fields from FloorPlansTab
          let width = parsed.dimensions?.width || 50; // default width
          let depth = parsed.dimensions?.length || 30; // default length/depth
          let squareFootage = parsed.squareFootage?.total || 1500; // default
          let stories = parsed.stories || 1;
          
          // If dimensions weren't saved, calculate from walls as fallback
          if ((!parsed.dimensions || !parsed.dimensions.length) && parsed.walls && parsed.walls.length > 0) {
            // Find min/max coordinates to determine dimensions
            let minX = Infinity, maxX = -Infinity;
            let minY = Infinity, maxY = -Infinity;
            
            parsed.walls.forEach((wall: any) => {
              minX = Math.min(minX, wall.startX || wall.x1 || 0, wall.endX || wall.x2 || 0);
              maxX = Math.max(maxX, wall.startX || wall.x1 || 0, wall.endX || wall.x2 || 0);
              minY = Math.min(minY, wall.startY || wall.y1 || 0, wall.endY || wall.y2 || 0);
              maxY = Math.max(maxY, wall.startY || wall.y1 || 0, wall.endY || wall.y2 || 0);
            });
            
            // Convert from grid units to feet (assuming 20 pixels per foot)
            width = Math.round((maxX - minX) / 20);
            depth = Math.round((maxY - minY) / 20);
            squareFootage = width * depth * stories;
          }
          
          // Use square footage from parsed data if available
          if (parsed.squareFootage?.total) {
            squareFootage = parsed.squareFootage.total;
          } else if (parsed.rooms && parsed.rooms.length > 0) {
            // Get actual square footage from rooms if available
            const totalArea = parsed.rooms.reduce((sum: number, room: any) => 
              sum + (room.area || room.squareFootage || 0), 0);
            if (totalArea > 0) {
              squareFootage = Math.round(totalArea * stories);
            }
          }
          
          // Count bedrooms and bathrooms from room data
          let bedrooms = 0;
          let bathrooms = 0;
          if (parsed.rooms) {
            parsed.rooms.forEach((room: any) => {
              if (room.type === 'Bedroom' || room.name?.toLowerCase().includes('bedroom')) {
                bedrooms++;
              } else if (room.type === 'Bathroom' || room.name?.toLowerCase().includes('bath')) {
                bathrooms++;
              }
            });
          }
          
          floorPlans.push({
            id: planInfo.id.toString(),
            name: parsed.name || planInfo.name || `Floor Plan ${planInfo.id}`,
            dimensions: { width, depth },
            squareFootage,
            bedrooms: bedrooms || undefined,
            bathrooms: bathrooms || undefined,
            walls: parsed.walls,
            rooms: parsed.rooms
          });
        } catch (error) {
          console.error(`Error loading floor plan ${planInfo.id}:`, error);
        }
      }
    });
    
    // If no saved floor plans, return sample ones
    if (floorPlans.length === 0) {
      return [
        {
          id: 'sample-ranch',
          name: 'Sample Ranch (No saved plans)',
          dimensions: { width: 50, depth: 30 },
          squareFootage: 1500,
          bedrooms: 3,
          bathrooms: 2
        }
      ];
    }
    
    return floorPlans;
  }
  
  /**
   * Create floor plan selector panel
   */
  private createFloorPlanSelector(): void {
    const selectorDiv = document.createElement('div');
    selectorDiv.id = 'floor-plan-selector';
    selectorDiv.style.cssText = `
      position: absolute;
      top: 10px;
      left: 10px;
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.2);
      padding: 12px;
      max-width: 350px;
      max-height: 500px;
      overflow-y: auto;
      z-index: 1000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
    `;
    
    // Load actual saved floor plans from localStorage
    const floorPlans = this.loadSavedFloorPlans();
    
    // Fallback sample floor plans if needed
    const sampleFloorPlans: FloorPlan[] = floorPlans.length > 0 ? floorPlans : [
      {
        id: 'craftsman-2800',
        name: 'Craftsman Home',
        dimensions: { width: 55, depth: 35 },
        squareFootage: 2800,
        bedrooms: 4,
        bathrooms: 3,
        stories: 2
      },
      {
        id: 'tiny-600',
        name: 'Tiny House',
        dimensions: { width: 20, depth: 30 },
        squareFootage: 600,
        bedrooms: 1,
        bathrooms: 1,
        stories: 1
      }
    ];
    
    selectorDiv.innerHTML = `
      <h3 style="margin: 0 0 12px 0; font-size: 16px; font-weight: 600; color: #FFFFFF; display: flex; align-items: center; gap: 8px;">
        <span style="color: #C084FC;">📐</span> Select Floor Plan
      </h3>
      ${floorPlans.length === 0 ? `
        <div style="padding: 12px; background: rgba(251, 191, 36, 0.15); border: 1px solid rgba(251, 191, 36, 0.3); border-radius: 8px; margin-bottom: 12px;">
          <p style="margin: 0; color: #FCD34D; font-size: 13px;">
            ℹ️ No saved floor plans found. Create floor plans in the Floor Plans tab first.
          </p>
        </div>
      ` : ''}
      <div id="floor-plan-list" style="display: grid; gap: 8px;">
        ${(floorPlans.length > 0 ? floorPlans : sampleFloorPlans).map(plan => `
          <div class="floor-plan-option" data-plan-id="${plan.id}" style="
            padding: 12px;
            border: 1px solid rgba(31, 41, 55, 1);
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.2s;
            background: linear-gradient(to bottom right, rgba(17, 24, 39, 0.5), rgba(31, 41, 55, 0.5));
            backdrop-filter: blur(4px);
          " onmouseover="this.style.borderColor='rgba(168, 85, 247, 0.5)'; this.style.background='linear-gradient(to bottom right, rgba(88, 28, 135, 0.2), rgba(59, 130, 246, 0.2))'"
             onmouseout="this.style.borderColor='rgba(31, 41, 55, 1)'; this.style.background='linear-gradient(to bottom right, rgba(17, 24, 39, 0.5), rgba(31, 41, 55, 0.5))'">
            <div style="display: flex; justify-content: space-between; align-items: start;">
              <div style="flex: 1;">
                <div style="font-weight: 600; color: #FFFFFF; margin-bottom: 6px;">
                  ${plan.name}
                </div>
                <div style="font-size: 12px; color: #D1D5DB; line-height: 1.6;">
                  <div style="margin-bottom: 4px;">
                    <input type="number" id="sqft-${plan.id}" value="${plan.squareFootage}"
                      style="width: 80px; padding: 4px 8px; background: rgba(31, 41, 55, 0.8); border: 1px solid rgba(31, 41, 55, 1); border-radius: 6px; color: #FFFFFF; font-size: 12px;"
                      onclick="event.stopPropagation();"
                      onfocus="this.style.borderColor='rgba(168, 85, 247, 0.5)'; this.style.outline='2px solid rgba(168, 85, 247, 0.2)';"
                      onblur="this.style.borderColor='rgba(31, 41, 55, 1)'; this.style.outline='none';"
                    /> sq ft
                  </div>
                  ${plan.bedrooms} bed / ${plan.bathrooms} bath<br/>
                  ${plan.dimensions.width}' × ${plan.dimensions.depth}'
                </div>
              </div>
              <div style="text-align: center; padding: 4px;">
                <div style="width: 50px; height: 40px; background: rgba(168, 85, 247, 0.1); border-radius: 6px; display: flex; align-items: center; justify-content: center;">
                  <span style="font-size: 24px;">🏠</span>
                </div>
                <div style="font-size: 10px; color: #9CA3AF; margin-top: 4px;">
                  ${plan.stories || 1} ${(plan.stories || 1) > 1 ? 'stories' : 'story'}
                </div>
              </div>
            </div>
          </div>
        `).join('')}
      </div>
      <div style="margin-top: 12px; padding: 12px; background: linear-gradient(to bottom right, rgba(17, 24, 39, 0.5), rgba(31, 41, 55, 0.5)); backdrop-filter: blur(4px); border-radius: 8px; border: 1px solid rgba(31, 41, 55, 1);">
        <div style="display: flex; gap: 8px; align-items: center;">
          <label style="font-size: 12px; color: #9CA3AF; font-weight: 500;">Rotation:</label>
          <input type="range" id="rotation-slider" min="0" max="360" value="0" style="flex: 1; accent-color: #A855F7;">
          <span id="rotation-value" style="font-size: 13px; width: 40px; color: #FFFFFF; font-weight: 600;">0°</span>
        </div>
      </div>
      <div id="selected-plan-info" style="
        margin-top: 12px;
        padding: 12px;
        background: rgba(34, 197, 94, 0.2);
        border: 1px solid rgba(34, 197, 94, 0.3);
        border-radius: 8px;
        display: none;
      ">
        <div style="font-weight: 600; color: #86EFAC; margin-bottom: 4px; font-size: 13px;">
          Selected: <span id="selected-plan-name"></span>
        </div>
        <div style="font-size: 12px; color: #D1D5DB;">
          Drag the floor plan on the map to position it
        </div>
      </div>
    `;
    
    this.map.controls[google.maps.ControlPosition.LEFT_TOP].push(selectorDiv);
    
    // Add event listeners
    setTimeout(() => {
      // Floor plan selection
      document.querySelectorAll('.floor-plan-option').forEach(option => {
        option.addEventListener('click', (e) => {
          const planId = (e.currentTarget as HTMLElement).dataset.planId;
          // Use the actual loaded floor plans
          const allPlans = floorPlans.length > 0 ? floorPlans : sampleFloorPlans;
          const plan = allPlans.find(p => p.id === planId);
          if (plan) {
            this.selectFloorPlan(plan);
          }
        });
      });
      
      // Rotation slider
      const rotationSlider = document.getElementById('rotation-slider') as HTMLInputElement;
      const rotationValue = document.getElementById('rotation-value');
      if (rotationSlider && rotationValue) {
        rotationSlider.addEventListener('input', (e) => {
          const value = (e.target as HTMLInputElement).value;
          this.rotation = parseInt(value);
          rotationValue.textContent = `${value}°`;
          if (this.floorPlanOverlay) {
            this.updateFloorPlanRotation();
          }
        });
      }

      // Square footage inputs
      const allPlans = floorPlans.length > 0 ? floorPlans : sampleFloorPlans;
      allPlans.forEach(plan => {
        const sqftInput = document.getElementById(`sqft-${plan.id}`) as HTMLInputElement;
        if (sqftInput) {
          sqftInput.addEventListener('change', (e) => {
            const newSqft = parseInt((e.target as HTMLInputElement).value);
            if (newSqft && newSqft > 0) {
              plan.squareFootage = newSqft;
              if (this.selectedFloorPlan && this.selectedFloorPlan.id === plan.id) {
                this.selectedFloorPlan.squareFootage = newSqft;
                this.updateMeasurements();
              }
            }
          });
        }
      });
    }, 100);
  }
  
  /**
   * Select and place a floor plan
   */
  private selectFloorPlan(plan: FloorPlan): void {
    this.selectedFloorPlan = plan;
    
    // Update UI
    const selectedInfo = document.getElementById('selected-plan-info');
    const selectedName = document.getElementById('selected-plan-name');
    if (selectedInfo && selectedName) {
      selectedName.textContent = plan.name;
      selectedInfo.style.display = 'block';
    }
    
    // Highlight selected option
    document.querySelectorAll('.floor-plan-option').forEach(option => {
      const optionEl = option as HTMLElement;
      if (optionEl.dataset.planId === plan.id) {
        optionEl.style.border = '2px solid #3b82f6';
        optionEl.style.background = '#dbeafe';
      } else {
        optionEl.style.border = '2px solid #e5e7eb';
        optionEl.style.background = 'white';
      }
    });
    
    // Place floor plan on map
    this.placeFloorPlan(plan);
  }
  
  /**
   * Place floor plan on the map
   */
  private placeFloorPlan(plan: FloorPlan): void {
    // Clear existing overlay
    if (this.floorPlanOverlay) {
      this.floorPlanOverlay.setMap(null);
    }
    
    // Calculate property center
    const bounds = new google.maps.LatLngBounds();
    this.propertyBoundary.forEach(point => bounds.extend(point));
    const center = bounds.getCenter();
    
    // Create floor plan polygon (rectangle for now, can be complex shape)
    const metersPerFoot = 0.3048;
    const latOffset = (plan.dimensions.depth * metersPerFoot) / 111320;
    const lngOffset = (plan.dimensions.width * metersPerFoot) / (111320 * Math.cos(center.lat() * Math.PI / 180));
    
    const corners = [
      new google.maps.LatLng(center.lat() - latOffset/2, center.lng() - lngOffset/2),
      new google.maps.LatLng(center.lat() - latOffset/2, center.lng() + lngOffset/2),
      new google.maps.LatLng(center.lat() + latOffset/2, center.lng() + lngOffset/2),
      new google.maps.LatLng(center.lat() + latOffset/2, center.lng() - lngOffset/2)
    ];
    
    this.floorPlanOverlay = new google.maps.Polygon({
      paths: corners,
      strokeColor: '#16a34a',
      strokeWeight: 2,
      fillColor: '#4ade80',
      fillOpacity: 0.35,
      draggable: true,
      editable: false,
      map: this.map,
      zIndex: 100
    });
    
    // Add drag listeners
    this.floorPlanOverlay.addListener('dragstart', () => {
      this.isDragging = true;
    });
    
    this.floorPlanOverlay.addListener('drag', () => {
      this.updateDistances();
    });
    
    this.floorPlanOverlay.addListener('dragend', () => {
      this.isDragging = false;
      this.updateDistances();
    });
    
    // Add click listener to show floor plan info
    this.floorPlanOverlay.addListener('click', (e: google.maps.PolyMouseEvent) => {
      const content = `
        <div style="padding: 8px; font-family: system-ui;">
          <h4 style="margin: 0 0 8px 0; font-weight: 600;">${plan.name}</h4>
          <div style="font-size: 13px; line-height: 1.5;">
            <strong>Size:</strong> ${plan.squareFootage.toLocaleString()} sq ft<br/>
            <strong>Dimensions:</strong> ${plan.dimensions.width}' × ${plan.dimensions.depth}'<br/>
            <strong>Bedrooms:</strong> ${plan.bedrooms}<br/>
            <strong>Bathrooms:</strong> ${plan.bathrooms}<br/>
            <strong>Stories:</strong> ${plan.stories}
          </div>
        </div>
      `;
      this.infoWindow.setContent(content);
      this.infoWindow.setPosition(e.latLng!);
      this.infoWindow.open(this.map);
    });
    
    // Calculate initial distances
    this.updateDistances();
    
    // Create measurement info panel
    this.createMeasurementPanel();
  }
  
  /**
   * Update floor plan rotation
   */
  private updateFloorPlanRotation(): void {
    if (!this.floorPlanOverlay || !this.selectedFloorPlan) return;
    
    // Get current center
    const path = this.floorPlanOverlay.getPath();
    let centerLat = 0, centerLng = 0;
    path.forEach((point, i) => {
      centerLat += point.lat();
      centerLng += point.lng();
    });
    centerLat /= path.getLength();
    centerLng /= path.getLength();
    const center = new google.maps.LatLng(centerLat, centerLng);
    
    // Calculate rotated corners
    const plan = this.selectedFloorPlan;
    const metersPerFoot = 0.3048;
    const latOffset = (plan.dimensions.depth * metersPerFoot) / 111320;
    const lngOffset = (plan.dimensions.width * metersPerFoot) / (111320 * Math.cos(center.lat() * Math.PI / 180));
    
    const angleRad = this.rotation * Math.PI / 180;
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);
    
    const corners = [
      [-lngOffset/2, -latOffset/2],
      [lngOffset/2, -latOffset/2],
      [lngOffset/2, latOffset/2],
      [-lngOffset/2, latOffset/2]
    ].map(([x, y]) => {
      const rotatedX = x * cos - y * sin;
      const rotatedY = x * sin + y * cos;
      return new google.maps.LatLng(
        center.lat() + rotatedY,
        center.lng() + rotatedX
      );
    });
    
    this.floorPlanOverlay.setPaths(corners);
    this.updateDistances();
  }
  
  /**
   * Create measurement panel
   */
  private createMeasurementPanel(): void {
    // Remove existing panel if any
    const existingPanel = document.getElementById('floor-plan-measurements');
    if (existingPanel) {
      existingPanel.remove();
    }
    
    const panel = document.createElement('div');
    panel.id = 'floor-plan-measurements';
    panel.style.cssText = `
      position: absolute;
      top: 10px;
      right: 10px;
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.2);
      padding: 16px;
      min-width: 320px;
      z-index: 1000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
    `;
    
    this.map.controls[google.maps.ControlPosition.RIGHT_TOP].push(panel);
    this.updateMeasurementPanel();
  }
  
  /**
   * Update distances to property lines
   */
  private updateDistances(): void {
    if (!this.floorPlanOverlay || !this.selectedFloorPlan) return;
    
    // Clear previous markers and lines
    this.clearDistanceMarkers();
    
    const floorPlanPath = this.floorPlanOverlay.getPath();
    const floorPlanCorners: google.maps.LatLng[] = [];
    floorPlanPath.forEach(point => floorPlanCorners.push(point));
    
    // Calculate center
    let centerLat = 0, centerLng = 0;
    floorPlanCorners.forEach(corner => {
      centerLat += corner.lat();
      centerLng += corner.lng();
    });
    centerLat /= floorPlanCorners.length;
    centerLng /= floorPlanCorners.length;
    const center = new google.maps.LatLng(centerLat, centerLng);
    
    // Find distances from each side to property lines
    const measurements: { [key: string]: number } = {};
    const directions = ['North', 'South', 'East', 'West'];
    
    directions.forEach(direction => {
      let testPoint: google.maps.LatLng;
      
      switch(direction) {
        case 'North':
          testPoint = floorPlanCorners.reduce((max, c) => c.lat() > max.lat() ? c : max);
          break;
        case 'South':
          testPoint = floorPlanCorners.reduce((min, c) => c.lat() < min.lat() ? c : min);
          break;
        case 'East':
          testPoint = floorPlanCorners.reduce((max, c) => c.lng() > max.lng() ? c : max);
          break;
        case 'West':
          testPoint = floorPlanCorners.reduce((min, c) => c.lng() < min.lng() ? c : min);
          break;
      }
      
      // Find nearest property line point
      let minDistance = Infinity;
      let nearestPoint: google.maps.LatLng | null = null;
      
      for (let i = 0; i < this.propertyBoundary.length; i++) {
        const j = (i + 1) % this.propertyBoundary.length;
        const nearestOnSegment = this.getNearestPointOnSegment(
          testPoint!,
          this.propertyBoundary[i],
          this.propertyBoundary[j]
        );
        const distance = this.calculateDistance(testPoint!, nearestOnSegment);
        
        if (distance < minDistance) {
          minDistance = distance;
          nearestPoint = nearestOnSegment;
        }
      }
      
      measurements[direction] = minDistance;
      
      if (nearestPoint) {
        // Draw distance line
        const line = new google.maps.Polyline({
          path: [testPoint!, nearestPoint],
          strokeColor: minDistance < this.requiredSetbacks.front ? '#ef4444' : '#3b82f6',
          strokeWeight: 2,
          strokeOpacity: 0.8,
          strokeDashArray: [5, 3],
          map: this.map,
          zIndex: 99
        });
        this.distanceLines.push(line);
        
        // Add distance label
        const labelPoint = this.getMidpoint(testPoint!, nearestPoint);
        const marker = new google.maps.Marker({
          position: labelPoint,
          map: this.map,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 0
          },
          label: {
            text: `${minDistance.toFixed(1)}'`,
            color: minDistance < this.requiredSetbacks.front ? '#dc2626' : '#1e40af',
            fontSize: '12px',
            fontWeight: 'bold'
          },
          zIndex: 101
        });
        this.distanceMarkers.push(marker);
      }
    });
    
    // Check setback violations
    this.checkSetbackViolations(measurements);
    
    // Update measurement panel
    this.updateMeasurementPanel(measurements);
  }
  
  /**
   * Check for setback violations
   */
  private checkSetbackViolations(measurements: { [key: string]: number }): void {
    this.setbackViolations = [];
    
    if (measurements.North < this.requiredSetbacks.front) {
      this.setbackViolations.push(`Front: ${measurements.North.toFixed(1)}' < ${this.requiredSetbacks.front}' required`);
    }
    if (measurements.South < this.requiredSetbacks.rear) {
      this.setbackViolations.push(`Rear: ${measurements.South.toFixed(1)}' < ${this.requiredSetbacks.rear}' required`);
    }
    if (measurements.East < this.requiredSetbacks.side || measurements.West < this.requiredSetbacks.side) {
      const minSide = Math.min(measurements.East, measurements.West);
      this.setbackViolations.push(`Side: ${minSide.toFixed(1)}' < ${this.requiredSetbacks.side}' required`);
    }
    
    // Update floor plan color based on violations
    if (this.floorPlanOverlay) {
      if (this.setbackViolations.length > 0) {
        this.floorPlanOverlay.setOptions({
          fillColor: '#ef4444',
          strokeColor: '#dc2626'
        });
      } else {
        this.floorPlanOverlay.setOptions({
          fillColor: '#4ade80',
          strokeColor: '#16a34a'
        });
      }
    }
  }
  
  /**
   * Update measurement panel
   */
  private updateMeasurementPanel(measurements?: { [key: string]: number }): void {
    const panel = document.getElementById('floor-plan-measurements');
    if (!panel || !this.selectedFloorPlan) return;
    
    const m = measurements || { North: 0, South: 0, East: 0, West: 0 };
    
    // Get actual GPS coordinates of floor plan corners if placed
    let cornerCoordinates = '';
    let stakingGuide = '';
    if (this.floorPlanOverlay) {
      const path = this.floorPlanOverlay.getPath();
      const corners: Array<{lat: number, lng: number}> = [];
      path.forEach(point => {
        corners.push({
          lat: point.lat(),
          lng: point.lng()
        });
      });
      
      // Get center point for reference
      let centerLat = 0, centerLng = 0;
      corners.forEach(corner => {
        centerLat += corner.lat;
        centerLng += corner.lng;
      });
      centerLat /= corners.length;
      centerLng /= corners.length;
      
      // Calculate property boundary extents for reference
      let northBoundary = -90, southBoundary = 90;
      let eastBoundary = -180, westBoundary = 180;
      this.propertyBoundary.forEach(point => {
        northBoundary = Math.max(northBoundary, point.lat());
        southBoundary = Math.min(southBoundary, point.lat());
        eastBoundary = Math.max(eastBoundary, point.lng());
        westBoundary = Math.min(westBoundary, point.lng());
      });
      
      cornerCoordinates = `
        <div style="margin-top: 12px; padding: 12px; background: rgba(31, 41, 55, 0.5); border-radius: 8px;">
          <div style="font-size: 11px; color: #D1D5DB; margin-bottom: 8px; line-height: 1.6;">
            <strong style="color: #FFFFFF;">GPS Center Point:</strong><br/>
            <span style="color: #9CA3AF;">Lat:</span> ${centerLat.toFixed(6)}°<br/>
            <span style="color: #9CA3AF;">Lng:</span> ${centerLng.toFixed(6)}°
          </div>
          <div style="font-size: 11px; color: #D1D5DB; margin-top: 12px; line-height: 1.6;">
            <strong style="color: #FFFFFF;">Corner Stakes (GPS):</strong><br/>
            ${corners.map((c, i) => `<span style="color: #9CA3AF;">${['NW', 'NE', 'SE', 'SW'][i]}:</span> ${c.lat.toFixed(6)}°, ${c.lng.toFixed(6)}°`).join('<br/>')}
          </div>
        </div>
      `;
      
      stakingGuide = `
        <strong>Field Staking Procedure:</strong><br/>
        1. Start at property NW corner (${northBoundary.toFixed(6)}°, ${westBoundary.toFixed(6)}°)<br/>
        2. Measure ${m.North.toFixed(1)}' south along west boundary<br/>
        3. Measure ${m.West.toFixed(1)}' east from west boundary<br/>
        4. Mark first stake at NW corner of foundation<br/>
        5. From NW stake, measure ${this.selectedFloorPlan.dimensions.width}' at ${this.rotation}° angle<br/>
        6. Mark NE stake, then measure ${this.selectedFloorPlan.dimensions.depth}' perpendicular<br/>
        7. Complete rectangle with SE and SW stakes<br/>
        8. Verify diagonals are equal: ${Math.sqrt(Math.pow(this.selectedFloorPlan.dimensions.width, 2) + Math.pow(this.selectedFloorPlan.dimensions.depth, 2)).toFixed(1)}' each<br/>
      `;
    } else {
      stakingGuide = `
        <strong>From Property Lines:</strong><br/>
        • ${m.North.toFixed(1)}' from north boundary<br/>
        • ${m.West.toFixed(1)}' from west boundary<br/>
        • ${m.South.toFixed(1)}' from south boundary<br/>
        • ${m.East.toFixed(1)}' from east boundary<br/>
        <br/>
        <strong>Foundation Layout:</strong><br/>
        • Footprint: ${this.selectedFloorPlan.dimensions.width}' × ${this.selectedFloorPlan.dimensions.depth}'<br/>
        • Rotation: ${this.rotation}° from north<br/>
        • Total area: ${this.selectedFloorPlan.squareFootage.toLocaleString()} sq ft<br/>
      `;
    }
    
    panel.innerHTML = `
      <h3 style="margin: 0 0 16px 0; font-size: 16px; font-weight: 600; color: #FFFFFF; display: flex; align-items: center; gap: 8px;">
        <span style="color: #C084FC;">📏</span> ${this.selectedFloorPlan.name} Placement
      </h3>

      <div style="margin-bottom: 16px; padding: 16px; background: linear-gradient(to bottom right, rgba(17, 24, 39, 0.5), rgba(31, 41, 55, 0.5)); backdrop-filter: blur(4px); border-radius: 12px; border: 1px solid rgba(31, 41, 55, 1);">
        <div style="font-size: 12px; color: #9CA3AF; margin-bottom: 8px; font-weight: 500;">Floor Plan Details:</div>
        <div style="font-size: 14px; color: #E5E7EB; line-height: 1.6;">
          <strong style="color: #FFFFFF;">${this.selectedFloorPlan.squareFootage.toLocaleString()} sq ft</strong><br/>
          ${this.selectedFloorPlan.dimensions.width}' × ${this.selectedFloorPlan.dimensions.depth}'<br/>
          ${this.selectedFloorPlan.bedrooms ? `${this.selectedFloorPlan.bedrooms} bed / ${this.selectedFloorPlan.bathrooms} bath` : 'Custom floor plan'}
        </div>
      </div>

      <h3 style="margin: 0 0 12px 0; font-weight: 600; color: #FFFFFF; display: flex; align-items: center; gap: 8px; font-size: 15px;">
        <span style="color: #C084FC;">📐</span> Distance to Property Lines
      </h3>

      <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 16px;">
        ${Object.entries(m).map(([dir, dist]) => {
          const isViolation =
            (dir === 'North' && dist < this.requiredSetbacks.front) ||
            (dir === 'South' && dist < this.requiredSetbacks.rear) ||
            ((dir === 'East' || dir === 'West') && dist < this.requiredSetbacks.side);
          const required =
            (dir === 'North' ? this.requiredSetbacks.front :
             dir === 'South' ? this.requiredSetbacks.rear :
             this.requiredSetbacks.side);

          return `
            <div style="background: rgba(31, 41, 55, 0.5); border-radius: 8px; padding: 12px;">
              <div style="font-size: 11px; color: #9CA3AF; margin-bottom: 4px;">${dir}:</div>
              <div style="font-size: 18px; font-weight: 700; color: #FFFFFF;">${dist.toFixed(1)} ft</div>
              <div style="font-size: 11px; margin-top: 4px; color: ${isViolation ? '#FCA5A5' : '#86EFAC'};">
                ${isViolation ? '✗' : '✓'} Setback: ${required} ft
              </div>
            </div>
          `;
        }).join('')}
      </div>

      ${this.setbackViolations.length > 0 ? `
        <div style="padding: 12px; background: rgba(239, 68, 68, 0.2); border-radius: 8px; border: 1px solid rgba(239, 68, 68, 0.3); margin-bottom: 16px;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="color: #FCA5A5; font-size: 20px;">⚠</span>
            <span style="color: #FCA5A5; font-weight: 600;">Setback Violation - Adjust Position</span>
          </div>
        </div>
      ` : `
        <div style="padding: 12px; background: rgba(34, 197, 94, 0.2); border-radius: 8px; border: 1px solid rgba(34, 197, 94, 0.3); margin-bottom: 16px;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="color: #86EFAC; font-size: 20px;">✓</span>
            <span style="color: #86EFAC; font-weight: 600;">All Setback Requirements Met</span>
          </div>
        </div>
      `}

      <div style="padding: 16px; background: linear-gradient(to bottom right, rgba(17, 24, 39, 0.5), rgba(31, 41, 55, 0.5)); backdrop-filter: blur(4px); border-radius: 12px; border: 1px solid rgba(31, 41, 55, 1);">
        <div style="font-size: 13px; font-weight: 600; color: #FFFFFF; margin-bottom: 12px; display: flex; align-items: center; gap: 8px;">
          <span style="color: #C084FC;">📍</span> Field Staking Guide
        </div>
        <div style="font-size: 12px; color: #D1D5DB; line-height: 1.7;">
          ${stakingGuide}
        </div>
        ${cornerCoordinates}
      </div>
    `;
  }
  
  // Utility methods (same as before)
  private calculateDistance(point1: google.maps.LatLng, point2: google.maps.LatLng): number {
    const R = 20902231; // Earth radius in feet
    const dLat = (point2.lat() - point1.lat()) * Math.PI / 180;
    const dLon = (point2.lng() - point1.lng()) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(point1.lat() * Math.PI / 180) * Math.cos(point2.lat() * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }
  
  private getMidpoint(point1: google.maps.LatLng, point2: google.maps.LatLng): google.maps.LatLng {
    return new google.maps.LatLng(
      (point1.lat() + point2.lat()) / 2,
      (point1.lng() + point2.lng()) / 2
    );
  }
  
  private getNearestPointOnSegment(point: google.maps.LatLng, start: google.maps.LatLng, end: google.maps.LatLng): google.maps.LatLng {
    const dx = end.lng() - start.lng();
    const dy = end.lat() - start.lat();
    const t = Math.max(0, Math.min(1, 
      ((point.lng() - start.lng()) * dx + (point.lat() - start.lat()) * dy) / 
      (dx * dx + dy * dy)
    ));
    
    return new google.maps.LatLng(
      start.lat() + t * dy,
      start.lng() + t * dx
    );
  }
  
  private clearDistanceMarkers(): void {
    this.distanceMarkers.forEach(marker => marker.setMap(null));
    this.distanceLines.forEach(line => line.setMap(null));
    this.distanceMarkers = [];
    this.distanceLines = [];
  }
  
  public disable(): void {
    if (this.floorPlanOverlay) {
      this.floorPlanOverlay.setMap(null);
    }
    this.clearDistanceMarkers();
    
    const selector = document.getElementById('floor-plan-selector');
    const measurements = document.getElementById('floor-plan-measurements');
    if (selector) selector.remove();
    if (measurements) measurements.remove();
  }
}

export default FloorPlanPlacementTool;